import { useCallback } from "react";
import { Controller } from "react-hook-form";

import { ContentTypes } from "api/types/knowledge";
import { ErrorMessage, InfoIcon, StyledModalText, UIButton } from "common/components";
import { Select } from "common/components/form/Select";
import { UIInput } from "common/components/form/UIInput";
import { useTranslate } from "common/utilities/useTranslate";
import { ModalHeader } from "domains/modal/components/ModalHeader";
import * as Styled from "domains/pinAddReject/components/AddKnowledgeModal/AddKnowledgeModal.styled";
import { useAddKnowledgeForm } from "domains/pinAddReject/components/AddKnowledgeModal/useAddKnowledgeForm";
import { useKnowledgeProcessing } from "domains/pinAddReject/components/AddKnowledgeModal/useKnowledgeProcessing";
import { KnowledgePreview } from "domains/pinAddReject/components/KnowledgePreview";

export const AddKnowledgeModal = () => {
  const translate = useTranslate();

  const {
    isAddKnowledgeLoading,
    isSubmitSuccessful,
    addedKnowledgeId,
    control,
    globalError,
    setGlobalError,
    submitNewKnowledge,
  } = useAddKnowledgeForm();

  const handleKnowledgeProcessingError = useCallback(
    () => setGlobalError(translate("app.add_knowledge.error.url_scraping_failed")),
    [setGlobalError, translate],
  );

  const { isTimeout, isProcessingComplete } = useKnowledgeProcessing({
    isEnabled: isSubmitSuccessful,
    knowledgeId: addedKnowledgeId,
    onKnowledgeProcessingError: handleKnowledgeProcessingError,
  });

  const showTimeoutMessage = isTimeout && !isProcessingComplete;

  const KNOWLEDGE_TYPE_OPTIONS = [
    {
      value: ContentTypes.ARTICLES_AND_PUBLICATION,
      label: translate("app.content_types.articles_and_publication"),
    },
    {
      value: ContentTypes.VIDEO,
      label: translate("app.discover.topic.tabs.videos.title"),
    },
    {
      value: ContentTypes.DATA_VISUALISATIONS,
      label: translate("app.discover.topic.tabs.interactive_data.title"),
    },
  ];

  return (
    <>
      <ModalHeader title={translate("app.add_knowledge.title")} />
      <Styled.Form onSubmit={submitNewKnowledge}>
        <StyledModalText>{translate("app.add_knowledge.description")}</StyledModalText>
        <Styled.FormFieldsContainer>
          <div>
            <Controller
              control={control}
              name="url"
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <Styled.InputContainer>
                  <UIInput
                    aria-labelledby="url-input-label"
                    name="Content URL Input"
                    onChange={onChange}
                    placeholder={translate("app.add_knowledge.url")}
                    value={value}
                    clear={() => onChange("")}
                    hasError={!!error}
                  />
                  <Styled.InputErrorMessage>{error?.message}</Styled.InputErrorMessage>
                </Styled.InputContainer>
              )}
            />
          </div>
          <div>
            <Controller
              control={control}
              name="knowledgeType"
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <Styled.InputContainer>
                  <Select
                    value={value}
                    placeholder={translate("app.add_knowledge.select_content_type")}
                    options={KNOWLEDGE_TYPE_OPTIONS.map((option) => ({
                      ...option,
                      onClick: () => onChange(option.value === value?.value ? undefined : option),
                    }))}
                    hasError={!!error}
                  />
                  <Styled.InputErrorMessage>{error?.message}</Styled.InputErrorMessage>
                  <Styled.InfoMessageContainer>
                    <div>
                      <InfoIcon width={16} height={16} />
                    </div>
                    <Styled.InfoMessage>{translate("app.add_knowledge.content_type.info")}</Styled.InfoMessage>
                  </Styled.InfoMessageContainer>
                </Styled.InputContainer>
              )}
            />
          </div>
        </Styled.FormFieldsContainer>
        <ErrorMessage isActive={!!globalError}>{globalError?.message}</ErrorMessage>
        {!isSubmitSuccessful ? (
          <UIButton fullwidth isLoading={isAddKnowledgeLoading} type="submit">
            {translate("app.add_knowledge.save")}
          </UIButton>
        ) : (
          <>
            {showTimeoutMessage && (
              <>
                <StyledModalText>
                  {translate("app.add_knowledge.processing.long_process_exit_and_return_later")}
                </StyledModalText>
                <StyledModalText>{translate("app.add_knowledge.processing.stay_for_realtime_updates")}</StyledModalText>
              </>
            )}
            <KnowledgePreview knowledgeId={addedKnowledgeId} isLoading={!isProcessingComplete} />
          </>
        )}
      </Styled.Form>
    </>
  );
};
